import React from 'react';
import { Package, MapPin, User, Phone, FileText, Search } from 'lucide-react';

const ManifestSection = ({ 
  data, 
  errors, 
  onChange, 
  onOpenModal, 
  originsData, 
  destinationsData 
}) => {
  // Función para obtener el nombre del origen seleccionado
  const getOriginName = (originId) => {
    if (!originId || !originsData?.origins) return 'Seleccionar origen';
    const origin = originsData.origins.find(o => o.id === originId);
    return origin ? origin.origin_name : 'Origen no encontrado';
  };

  // Función para obtener el nombre del destino seleccionado
  const getDestinationName = (destinationId) => {
    if (!destinationId || !destinationsData?.destinations) return 'Seleccionar destino';
    const destination = destinationsData.destinations.find(d => d.id === destinationId);
    return destination ? destination.destination_name : 'Destino no encontrado';
  };

  return (
    <div className="bg-blue-50 rounded-lg shadow-sm border border-blue-200 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Package className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-blue-900">Información del Manifiesto</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Número de Manifiesto */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-blue-900">
            Número de Manifiesto *
          </label>
          <input
            type="text"
            value={data.manifest}
            onChange={(e) => onChange('manifest', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.manifest ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ingrese el número de manifiesto"
          />
          {errors.manifest && (
            <p className="text-red-600 text-sm">{errors.manifest}</p>
          )}
        </div>

        {/* Referencia */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-blue-900">
            Referencia *
          </label>
          <input
            type="text"
            value={data.reference}
            onChange={(e) => onChange('reference', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.reference ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ingrese la referencia"
          />
          {errors.reference && (
            <p className="text-red-600 text-sm">{errors.reference}</p>
          )}
        </div>

        {/* Origen */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-blue-900">
            Origen *
          </label>
          <button
            type="button"
            onClick={() => onOpenModal('origins')}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-left flex items-center justify-between ${
              errors.originId ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <span className={data.originId ? 'text-gray-900' : 'text-gray-500'}>
              {getOriginName(data.originId)}
            </span>
            <Search className="w-4 h-4 text-gray-400" />
          </button>
          {errors.originId && (
            <p className="text-red-600 text-sm">{errors.originId}</p>
          )}
        </div>

        {/* Destino */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-blue-900">
            Destino *
          </label>
          <button
            type="button"
            onClick={() => onOpenModal('destinations')}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-left flex items-center justify-between ${
              errors.destinationId ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <span className={data.destinationId ? 'text-gray-900' : 'text-gray-500'}>
              {getDestinationName(data.destinationId)}
            </span>
            <Search className="w-4 h-4 text-gray-400" />
          </button>
          {errors.destinationId && (
            <p className="text-red-600 text-sm">{errors.destinationId}</p>
          )}
        </div>

        {/* Conductor */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-blue-900">
            Conductor *
          </label>
          <input
            type="text"
            value={data.driver}
            onChange={(e) => onChange('driver', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.driver ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Nombre del conductor"
          />
          {errors.driver && (
            <p className="text-red-600 text-sm">{errors.driver}</p>
          )}
        </div>

        {/* Pasaporte del Conductor */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-blue-900">
            Pasaporte del Conductor
          </label>
          <input
            type="text"
            value={data.driver_passport}
            onChange={(e) => onChange('driver_passport', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Número de pasaporte"
          />
        </div>

        {/* Licencia del Conductor */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-blue-900">
            Licencia del Conductor
          </label>
          <input
            type="text"
            value={data.driver_license}
            onChange={(e) => onChange('driver_license', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Número de licencia"
          />
        </div>

        {/* Teléfono del Conductor */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-blue-900">
            Teléfono del Conductor
          </label>
          <input
            type="tel"
            value={data.driver_phone}
            onChange={(e) => onChange('driver_phone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Número de teléfono"
          />
        </div>

        {/* Notas - Ocupa toda la fila */}
        <div className="space-y-2 md:col-span-2 lg:col-span-3">
          <label className="block text-sm font-medium text-blue-900">
            Notas
          </label>
          <textarea
            value={data.notes}
            onChange={(e) => onChange('notes', e.target.value)}
            rows={3}
            maxLength={500}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Notas adicionales del envío (máximo 500 caracteres)"
          />
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">
              {data.notes.length}/500 caracteres
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManifestSection;